import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { sr } from "../../lib/sr";
import { Loader2, ShieldAlert, Search, Save, DollarSign } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function CapRow({ c, onSaved }) {
  const [daily, setDaily] = useState(c.spend_cap_daily ? String(c.spend_cap_daily) : "");
  const [monthly, setMonthly] = useState(c.spend_cap_monthly ? String(c.spend_cap_monthly) : "");
  const [busy, setBusy] = useState(false);
  const over = c.spend_cap_monthly > 0 && c.mtd_spend >= c.spend_cap_monthly;
  const save = async () => {
    setBusy(true);
    try {
      await sr.setClientSpendCap(c.tenant_id, { daily_cap: parseFloat(daily) || 0, monthly_cap: parseFloat(monthly) || 0 });
      toast.success(`Caps saved for ${c.name}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save cap"); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr 1fr 90px", gap: 8, padding: "10px 14px", alignItems: "center", borderTop: "1px solid var(--border)", background: over ? "#FEF3F2" : "#fff" }} data-testid={`cap-row-${c.tenant_id}`}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 700, color: "var(--navy)", flexWrap: "wrap" }}>
        {c.name}{c.category && <span className="sr-badge sr-badge-gray">{c.category}</span>}
      </span>
      <span data-testid={`cap-mtd-${c.tenant_id}`} style={{ color: over ? "#B42318" : "var(--navy)", fontWeight: over ? 700 : 400 }}>
        {money(c.mtd_spend)}{over && " ⚠"}
      </span>
      <input className="sr-input" type="number" min="0" placeholder="No cap" value={daily}
        onChange={(e) => setDaily(e.target.value)} data-testid={`cap-daily-${c.tenant_id}`} style={{ maxWidth: 110 }} />
      <input className="sr-input" type="number" min="0" placeholder="No cap" value={monthly}
        onChange={(e) => setMonthly(e.target.value)} data-testid={`cap-monthly-${c.tenant_id}`} style={{ maxWidth: 110 }} />
      <button className="sr-btn sr-btn-sm sr-btn-primary" disabled={busy} onClick={save} data-testid={`cap-save-${c.tenant_id}`}>
        {busy ? <Loader2 size={13} className="sr-spin" /> : <Save size={13} />}
      </button>
    </div>
  );
}

export default function AdminSpendCaps() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [guardBusy, setGuardBusy] = useState(false);
  const [guardCap, setGuardCap] = useState("");

  const load = () => sr.adminSpendCaps().then((d) => { setData(d); setGuardCap(d.guard?.daily_cap ? String(d.guard.daily_cap) : ""); }).catch(() => setData({ clients: [] }));
  useEffect(() => { load(); }, []);

  const saveGuard = async (enabled) => {
    setGuardBusy(true);
    try {
      const g = await sr.adminSpendGuardSet({ enabled, daily_cap: parseFloat(guardCap) || 0 });
      toast.success(enabled ? `Global auto-pause on at ${money(g.daily_cap)}/day` : "Global auto-pause off");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update guard"); }
    finally { setGuardBusy(false); }
  };

  const rows = (data?.clients || []).filter((c) => !q || (c.name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-spend-caps">
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 24, color: "var(--navy)", display: "flex", alignItems: "center", gap: 8 }}>
          <ShieldAlert size={22} style={{ color: "#B42318" }} /> Ad-Spend Caps
        </h1>
        <p className="sr-muted" style={{ marginTop: 4 }}>
          Set a monthly and/or daily Meta ad-spend cap per client. Campaigns are automatically paused when a cap is reached{data?.month ? ` — showing spend for ${data.month}` : ""}.
        </p>
      </div>

      <div className="sr-card" style={{ marginBottom: 14, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", borderLeft: "3px solid #B42318" }} data-testid="global-guard-bar">
        <strong style={{ color: "var(--navy)" }}>Global daily auto-pause</strong>
        <span className="sr-muted" style={{ fontSize: 13 }}>Fallback cap for clients without their own.</span>
        <input className="sr-input" type="number" min="0" placeholder="$ / day" value={guardCap} onChange={(e) => setGuardCap(e.target.value)} data-testid="global-guard-cap" style={{ maxWidth: 120 }} />
        {data?.guard?.enabled ? (
          <>
            <span className="sr-badge sr-badge-green" data-testid="global-guard-on">On · {money(data.guard.daily_cap)}/day</span>
            <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={guardBusy} onClick={() => saveGuard(false)} data-testid="global-guard-off">Turn off</button>
            <button className="sr-btn sr-btn-sm" disabled={guardBusy} onClick={() => saveGuard(true)} data-testid="global-guard-update">Update</button>
          </>
        ) : (
          <button className="sr-btn sr-btn-sm sr-btn-primary" disabled={guardBusy} onClick={() => saveGuard(true)} data-testid="global-guard-enable">
            {guardBusy ? <Loader2 size={13} className="sr-spin" /> : <DollarSign size={13} />} Enable
          </button>
        )}
      </div>

      <div style={{ position: "relative", maxWidth: 320, marginBottom: 12 }}>
        <Search size={15} style={{ position: "absolute", left: 10, top: 11, color: "var(--muted)" }} />
        <input className="sr-input" style={{ paddingLeft: 32 }} placeholder="Search clients" value={q} onChange={(e) => setQ(e.target.value)} data-testid="cap-search" />
      </div>

      {!data ? <div className="sr-empty"><Loader2 className="sr-spin" /></div> : (
        <div className="sr-card" style={{ padding: 0, overflow: "hidden" }} data-testid="cap-table">
          <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr 1fr 90px", gap: 8, padding: "10px 14px", background: "var(--blue-light)", fontSize: 12, fontWeight: 700, color: "var(--navy)" }}>
            <span>Client</span><span>Spend this month</span><span>Daily cap ($)</span><span>Monthly cap ($)</span><span></span>
          </div>
          {rows.length === 0 ? <div className="sr-empty" style={{ padding: 24 }}>No clients match.</div>
            : rows.map((c) => <CapRow key={c.tenant_id} c={c} onSaved={load} />)}
        </div>
      )}
    </div>
  );
}
